import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TaskDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Chip, PageHeader, Panel, toneColor } from "@/design/primitives";
import { DataTable, type Column } from "@/design/DataTable";
import { relativeTime } from "@/lib/format";
import { useAuth } from "@/auth/auth";
import { useRealtimeEvent } from "@/realtime/useRealtime";

/** Execution Plan (spec §36, /Design): NOW/NEXT/THEN lane + globální fronta. */
export function PlanPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["plan"], queryFn: api.plan, refetchInterval: 30_000 });
  const { session } = useAuth();
  useRealtimeEvent((m) => {
    if (m.type === "project.section.changed" && m.section === "tasks") {
      void queryClient.invalidateQueries({ queryKey: ["plan"] });
    }
  });

  const pinMutation = useMutation({
    mutationFn: (taskId: string) => api.pinTask(taskId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["plan"] }),
  });

  const plan = query.data;
  if (!plan) return null;
  const isOwner = session?.user.role === "OWNER";
  const queueRows = [...plan.next, ...plan.then, ...plan.queued];

  const queueColumns: Column<(typeof queueRows)[number]>[] = [
            {
              key: "ep",
              label: cs.plan.ep,
              width: "56px",
              cell: (t) => (
                <span
                  className="af-display text-[15px] font-bold"
                  style={{
                    color: t.pinnedAt
                      ? "var(--warning)"
                      : t.epScore >= 70
                        ? "var(--critical)"
                        : "var(--accent)",
                  }}
                >
                  {t.epScore}
                </span>
              ),
            },
            {
              key: "task",
              label: cs.plan.taskCol,
              primary: true,
              cell: (t) => (
                <>
                  <Link
                    to="/projects/$slug/tasks/$number"
                    params={{ slug: t.projectSlug, number: String(t.number) }}
                    style={{ color: "var(--text-primary)" }}
                  >
                    #{t.number} {t.title}
                  </Link>
                  {t.pinnedAt ? (
                    <span className="ml-2">
                      <Chip tone="amber" filled>
                        {cs.plan.override}
                      </Chip>
                    </span>
                  ) : null}
                </>
              ),
            },
            {
              key: "project",
              label: cs.plan.project,
              width: "minmax(110px, 130px)",
              cell: (t) => (
                <Link to="/projects/$slug" params={{ slug: t.projectSlug }} style={{ color: "var(--accent)" }}>
                  {t.projectName}
                </Link>
              ),
            },
            {
              key: "why",
              label: cs.plan.why,
              width: "minmax(180px, 1.1fr)",
              cell: (t) => (
                <span style={{ color: "var(--text-tertiary)" }}>
                  {t.epBreakdown.map((f) => `${f.label} +${f.value}`).join(" · ")}
                </span>
              ),
            },
            {
              key: "action",
              label: "",
              width: "120px",
              align: "right",
              cell: (t) =>
                isOwner && !t.pinnedAt ? (
                  <Btn
                    variant="danger"
                    tone="amber"
                    onClick={() => pinMutation.mutate(t.id)}
                    data-testid="plan-do-next"
                  >
                    {cs.plan.doNext}
                  </Btn>
                ) : null,
            },
  ];

  return (
    <div className="af-animate-fade">
      <PageHeader
        title={cs.plan.title}
        kicker="EXECUTION"
        lead={cs.plan.subtitle}
        right={
          <span className="af-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.plan.computed(relativeTime(plan.computedAt))}
          </span>
        }
      />

      {/* Factory lane (worker lanes ve Fázi 4) */}
      <Panel className="mb-4">
        <div className="flex flex-wrap justify-between gap-2 px-4 py-2.5" style={{ borderBottom: "1px solid color-mix(in srgb, var(--accent) 8%, transparent)" }}>
          <span className="af-display text-[11px] font-semibold" style={{ color: "var(--text-primary)" }}>
            {cs.plan.lane}
          </span>
          <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.plan.laneCaps}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3">
          <LaneBox tone="green" label={cs.plan.now} pulse>
            {plan.now.length === 0 ? (
              <span style={{ color: "var(--text-tertiary)" }}>{cs.plan.idle}</span>
            ) : (
              plan.now.map((t) => <LaneTask key={t.id} task={t} />)
            )}
          </LaneBox>
          <LaneBox tone="cyan" label={cs.plan.next}>
            {plan.next.map((t) => (
              <LaneTask key={t.id} task={t} showWhy />
            ))}
            {plan.next.length === 0 ? <span style={{ color: "var(--text-tertiary)" }}>—</span> : null}
          </LaneBox>
          <LaneBox tone="dim" label={cs.plan.then}>
            {plan.then.map((t) => (
              <LaneTask key={t.id} task={t} />
            ))}
            {plan.then.length === 0 ? <span style={{ color: "var(--text-tertiary)" }}>—</span> : null}
          </LaneBox>
        </div>
      </Panel>

      {/* globální fronta */}
      <Panel className="p-4">
        <div className="flex justify-between text-[11px] tracking-[0.18em]">
          <span style={{ color: "var(--accent)" }}>{cs.plan.queued}</span>
          <span style={{ color: "var(--text-tertiary)" }}>{cs.plan.acrossProjects}</span>
        </div>
        <div className="mb-3 mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.plan.queuedNote}
          {plan.blockedCount ? ` · ${cs.plan.blocked(plan.blockedCount)}` : ""}
        </div>
        <DataTable
          columns={queueColumns}
          rows={queueRows}
          rowKey={(t) => t.id}
          rowTestId={() => "plan-row"}
          stackBelow={1024}
        />
        {queueRows.length === 0 ? (
          <div className="py-4 text-center text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            Fronta je prázdná.
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

function LaneBox({
  tone,
  label,
  pulse,
  children,
}: {
  tone: "green" | "cyan" | "dim";
  label: string;
  pulse?: boolean;
  children: React.ReactNode;
}) {
  const color = tone === "dim" ? "var(--text-tertiary)" : toneColor[tone];
  return (
    <div className="px-3.5 py-3" style={{ borderLeft: `2px solid ${tone === "dim" ? "var(--border-default)" : color}`, background: tone === "green" ? "color-mix(in srgb, var(--positive) 3%, transparent)" : "transparent" }}>
      <div className="mb-1.5 text-[11px] tracking-[0.18em]" style={{ color }}>
        {label} {pulse ? <span className="af-animate-pulse">●</span> : null}
      </div>
      <div className="flex flex-col gap-1.5 text-[12px]">{children}</div>
    </div>
  );
}

function LaneTask({ task, showWhy }: { task: TaskDTO; showWhy?: boolean }) {
  return (
    <div>
      <Link
        to="/projects/$slug/tasks/$number"
        params={{ slug: task.projectSlug, number: String(task.number) }}
        className="text-[12px]"
        style={{ color: "var(--text-primary)" }}
      >
        {task.projectName} → #{task.number} {task.title}
      </Link>
      <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        EP {task.epScore} · {task.departmentName ?? "bez oddělení"}
        {showWhy && task.epBreakdown[0] ? ` · ${task.epBreakdown.slice(0, 2).map((f) => f.label).join(", ")}` : ""}
      </div>
    </div>
  );
}
